import { useState } from "react";
import { tokenService } from "@/services/tokenService";
import { useToast } from "@/hooks/use-toast";
import { useMembershipStatus } from "@/hooks/useMembershipStatus";

export const TOKEN_COSTS = {
  'document-generation': 500,
  'ai-chat': 100,
  'workflow-step': 150,
  'law-search': 100,
  'case-creation': 800,
  'help-assistant': 100
} as const;

export type TokenAction = keyof typeof TOKEN_COSTS;

export function useTokenManagement() {
  const [isChecking, setIsChecking] = useState(false);
  const { toast } = useToast();
  const { membershipLevel, hasEnoughTokens, canUseFeature, profile } = useMembershipStatus();

  const isAdmin = profile?.notification_preferences?.is_admin === true;

  const checkTokens = async (action: TokenAction, _service?: string) => {
    // Superadmin users bypass all token checks
    if (isAdmin) {
      return true;
    }

    setIsChecking(true);
    try {
      const requiredTokens = TOKEN_COSTS[action];
      const hasTokens = hasEnoughTokens(requiredTokens);

      if (!hasTokens) {
        toast({
          title: "Ikke nok tokens",
          description: "Du har ikke nok tokens til å utføre denne handlingen.",
          variant: "destructive",
        });
        return false;
      }

      return true;
    } finally {
      setIsChecking(false);
    }
  };

  const recordTokenUsage = async (action: TokenAction, _page?: string) => {
    // Superadmin users don't record token usage
    if (isAdmin) {
      return;
    }

    try {
      await tokenService.recordTokenUsage({
        page: action,
        action: 'use',
        tokensUsed: TOKEN_COSTS[action]
      });
    } catch (error) {
      console.error('Failed to record token usage:', error);
      toast({
        title: "Feil ved registrering av tokens",
        description: "Kunne ikke registrere token-bruk. Prøv igjen senere.",
        variant: "destructive",
      });
    }
  };

  return {
    isChecking,
    checkTokens,
    recordTokenUsage,
    membershipLevel,
    canUseFeature,
    TOKEN_COSTS,
    isAdmin
  };
}